import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { NavTab, NAV_TABS_KEY } from '../navigation-tabs.interfaces';
import { NavTabsStoreService } from './nav-tabs-store.service';

@Injectable()
export class NavTabsService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly navTabsStoreService = inject(NavTabsStoreService);

  initRouteListener(): void {
    this.buildNavTabs();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.buildNavTabs();
      });
  }

  private buildNavTabs(): void {
    let child = this.route;
    let routeWithTabs: ActivatedRoute | null = null;

    // Find where NAV_TABS_KEY is first defined
    while (child.firstChild) {
      if (child.snapshot?.data?.[NAV_TABS_KEY]) {
        routeWithTabs = child;
        break;
      }
      child = child.firstChild;
    }
    // Check the deepest child only if no parent defined it
    if (!routeWithTabs && child.snapshot?.data?.[NAV_TABS_KEY]) {
      routeWithTabs = child;
    }

    const tabs = routeWithTabs?.snapshot?.data?.[NAV_TABS_KEY] as NavTab[] | undefined;
    if (!tabs?.length || !routeWithTabs) {
      this.navTabsStoreService.setTabs([]);
      return;
    }

    const basePath = this.buildBasePath(routeWithTabs);

    this.navTabsStoreService.setTabs(
      tabs.map(tab => ({
        ...tab,
        route: this.buildTabRoute(String(tab.route), basePath),
      }))
    );
  }

  private buildTabRoute(tabRoute: string, basePath: string): string {
    // Routes starting with '.' navigate to a sibling of the current tab route
    // e.g., '.members' at /teams/123/projects/456/tasks/789 → /teams/123/projects/456/members
    if (tabRoute.startsWith('.')) {
      const newSegment = tabRoute.slice(1);
      return this.joinRouteSegments(basePath, newSegment);
    }

    // Exact '/' means navigate to root
    if (tabRoute === '/') {
      return '/';
    }

    // Routes starting with '/' indicate an absolute-like path
    // Find the segment in the current URL and build path up to it
    if (tabRoute.startsWith('/')) {
      const segmentName = tabRoute.slice(1).split('/')[0];
      const absolutePath = this.buildPathToSegment(segmentName);
      if (absolutePath) {
        // Append any remaining path after the segment name
        const remainingPath = tabRoute.slice(1 + segmentName.length);
        return remainingPath ? absolutePath + remainingPath : absolutePath;
      }
    }

    return this.joinRouteSegments(basePath, tabRoute);
  }

  private buildPathToSegment(segmentName: string): string | null {
    const segments: string[] = [];
    let current: ActivatedRoute | null = this.route;
    let foundSegment = false;

    while (current) {
      for (const seg of current.snapshot.url) {
        segments.push(seg.path);

        if (seg.path === segmentName) {
          foundSegment = true;
          // Include the next segment if it looks like an ID (param value)
          const nextSegments = current.firstChild?.snapshot.url ?? [];
          if (nextSegments.length > 0) {
            segments.push(nextSegments[0].path);
          }
          break;
        }
      }

      if (foundSegment) break;
      current = current.firstChild ?? null;
    }

    if (!foundSegment) return null;

    return '/' + segments.filter(Boolean).join('/');
  }

  private joinRouteSegments(...segments: string[]): string {
    const cleanedSegments = segments
      .filter(segment => segment != null && segment !== '')
      .map((segment, index) => {
        let value = segment;

        if (index === 0) {
          value = value.replaceAll(/\/+$/g, '');
        } else {
          value = value.replaceAll(/(^\/+|\/+$)/g, '');
        }

        return value;
      })
      .filter(Boolean);

    if (!cleanedSegments.length) {
      return '/';
    }

    const path = cleanedSegments.join('/');

    return path.startsWith('/') ? path : `/${path}`;
  }

  private buildBasePath(deepestChild: ActivatedRoute): string {
    const segments: string[] = [];
    let current: ActivatedRoute | null = this.route;

    while (current && current !== deepestChild) {
      for (const seg of current.snapshot.url) {
        segments.push(seg.path);
      }
      current = current.firstChild ?? null;
    }

    return '/' + segments.filter(Boolean).join('/');
  }
}
